import React, { useEffect, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    listfixedassets,
    viewfixedasset,
    createfixedasset,
    updatefixedasset,
    deletefixedasset,
    recorddepreciation,
    recorddisposal,
    getassetregister,
    listassetcategories,
    listfinancialyears,
    listbranch
} from "../../../../services/admin";
import { toast } from "react-toastify";
import moment from "moment";
import Swal from "sweetalert2";

export default function AllFixedAssets() {
    const [assets, setAssets] = useState([]);
    const [categories, setCategories] = useState([]);
    const [financialYears, setFinancialYears] = useState([]);
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('list'); // list, create, depreciate, dispose, register
    const [editingAsset, setEditingAsset] = useState(null);
    const [selectedAsset, setSelectedAsset] = useState(null);

    // Asset Form Data
    const [assetFormData, setAssetFormData] = useState({
        asset_name: "",
        asset_category_id: "",
        branch_id: "",
        purchase_date: "",
        purchase_cost: "",
        salvage_value: "0",
        location: "",
        serial_number: "",
        manufacturer: "",
        model: "",
        warranty_months: "",
        remarks: ""
    });

    // Depreciation Form Data
    const [depreciationFormData, setDepreciationFormData] = useState({
        asset_id: "",
        financial_year_id: "",
        depreciation_date: moment().format('YYYY-MM-DD'),
        period: "Annual",
        create_voucher: true,
        remarks: ""
    });

    // Disposal Form Data
    const [disposalFormData, setDisposalFormData] = useState({
        asset_id: "",
        disposal_date: moment().format('YYYY-MM-DD'),
        disposal_type: "Sale",
        disposal_value: "",
        buyer_name: "",
        disposal_remarks: "",
        create_voucher: true
    });

    // Filters
    const [filters, setFilters] = useState({
        category_id: "",
        status: "Active"
    });

    useEffect(() => {
        fetchAssets();
        fetchCategories();
        fetchFinancialYears();
        fetchBranches();
    }, []);

    const fetchAssets = (filterParams = {}) => {
        setLoading(true);
        const branchId = localStorage.getItem("branch") || "";
        const params = {
            ...filters,
            ...filterParams,
            branch_id: branchId
        };
        listfixedassets(params).then((res) => {
            if (res.status === true) {
                setAssets(res.data);
            }
            setLoading(false);
        });
    };

    const fetchCategories = () => {
        listassetcategories().then((res) => {
            if (res.status === true) {
                setCategories(res.data.filter(c => c.is_active));
            }
        });
    };

    const fetchFinancialYears = () => {
        listfinancialyears().then((res) => {
            if (res.status === true) {
                setFinancialYears(res.data);
                // Auto-select active year
                const activeYear = res.data.find(y => y.is_active);
                if (activeYear) {
                    setDepreciationFormData(prev => ({ ...prev, financial_year_id: activeYear.id }));
                }
            }
        });
    };

    const fetchBranches = () => {
        listbranch(1).then((res) => {
            if (res.status === true) {
                setBranches(res.data);
            }
        });
    };

    const handleAssetInputChange = (e) => {
        const { name, value } = e.target;
        setAssetFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleDepreciationInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDepreciationFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleDisposalInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setDisposalFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        const newFilters = { ...filters, [name]: value };
        setFilters(newFilters);
        fetchAssets(newFilters);
    };

    const handleAssetSubmit = (e) => {
        e.preventDefault();

        if (!assetFormData.asset_name || !assetFormData.asset_category_id || !assetFormData.purchase_date || !assetFormData.purchase_cost) {
            toast.error("Please fill all required fields");
            return;
        }

        // Get branch from localStorage
        const branchId = localStorage.getItem("branch") || "";
        const submitData = {
            ...assetFormData,
            branch_id: branchId
        };

        if (editingAsset) {
            updatefixedasset(editingAsset.id, submitData).then((res) => {
                if (res.status === true) {
                    toast.success("Fixed Asset Updated Successfully");
                    fetchAssets();
                    resetAssetForm();
                } else {
                    toast.error(res.message || "Failed to update");
                }
            });
        } else {
            createfixedasset(submitData).then((res) => {
                if (res.status === true) {
                    toast.success("Fixed Asset Created Successfully");
                    fetchAssets();
                    resetAssetForm();
                } else {
                    toast.error(res.message || "Failed to create");
                }
            });
        }
    };

    const handleDepreciationSubmit = (e) => {
        e.preventDefault();

        if (!depreciationFormData.asset_id || !depreciationFormData.financial_year_id) {
            toast.error("Please select asset and financial year");
            return;
        }

        recorddepreciation(depreciationFormData).then((res) => {
            if (res.status === true) {
                toast.success("Depreciation Recorded Successfully");
                fetchAssets();
                setActiveTab('list');
                resetDepreciationForm();
            } else {
                toast.error(res.message || "Failed to record depreciation");
            }
        });
    };

    const handleDisposalSubmit = (e) => {
        e.preventDefault();

        if (!disposalFormData.asset_id || !disposalFormData.disposal_date) {
            toast.error("Please fill all required fields");
            return;
        }

        Swal.fire({
            title: 'Confirm Asset Disposal?',
            html: `<p>This action will mark the asset as disposed and cannot be undone.</p>
                   <p>Are you sure you want to proceed?</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, dispose it!'
        }).then((result) => {
            if (result.isConfirmed) {
                recorddisposal(disposalFormData).then((res) => {
                    if (res.status === true) {
                        toast.success("Asset Disposal Recorded Successfully");
                        fetchAssets();
                        setActiveTab('list');
                        resetDisposalForm();
                    } else {
                        toast.error(res.message || "Failed to record disposal");
                    }
                });
            }
        });
    };

    const handleEdit = (asset) => {
        setEditingAsset(asset);
        setAssetFormData({
            asset_name: asset.asset_name,
            asset_category_id: asset.asset_category_id,
            branch_id: asset.branch_id || "",
            purchase_date: moment(asset.purchase_date).format('YYYY-MM-DD'),
            purchase_cost: asset.purchase_cost,
            salvage_value: asset.salvage_value,
            location: asset.location || "",
            serial_number: asset.serial_number || "",
            manufacturer: asset.manufacturer || "",
            model: asset.model || "",
            warranty_months: asset.warranty_months || "",
            remarks: asset.remarks || ""
        });
        setActiveTab('create');
    };

    const handleDelete = (asset) => {
        Swal.fire({
            title: 'Delete Fixed Asset?',
            text: `Delete ${asset.asset_name}? This cannot be undone!`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        }).then((result) => {
            if (result.isConfirmed) {
                deletefixedasset(asset.id).then((res) => {
                    if (res.status === true) {
                        toast.success("Fixed Asset Deleted Successfully");
                        fetchAssets();
                    } else {
                        toast.error(res.message || "Failed to delete");
                    }
                });
            }
        });
    };

    const handleDepreciate = (asset) => {
        setSelectedAsset(asset);
        setDepreciationFormData(prev => ({
            ...prev,
            asset_id: asset.id
        }));
        // Fetch fresh asset list - clear filters to get all active assets
        fetchAssets({ category_id: "", status: 'Active' });
        setActiveTab('depreciate');
    };

    const handleDispose = (asset) => {
        setSelectedAsset(asset);
        setDisposalFormData(prev => ({
            ...prev,
            asset_id: asset.id,
            disposal_value: asset.book_value
        }));
        // Fetch fresh asset list - clear filters to get all active assets
        fetchAssets({ category_id: "", status: 'Active' });
        setActiveTab('dispose');
    };

    const resetAssetForm = () => {
        setAssetFormData({
            asset_name: "",
            asset_category_id: "",
            branch_id: "",
            purchase_date: "",
            purchase_cost: "",
            salvage_value: "0",
            location: "",
            serial_number: "",
            manufacturer: "",
            model: "",
            warranty_months: "",
            remarks: ""
        });
        setEditingAsset(null);
        setActiveTab('list');
    };

    const resetDepreciationForm = () => {
        const activeYear = financialYears.find(y => y.is_active);
        setDepreciationFormData({
            asset_id: "",
            financial_year_id: activeYear?.id || "",
            depreciation_date: moment().format('YYYY-MM-DD'),
            period: "Annual",
            create_voucher: true,
            remarks: ""
        });
        setSelectedAsset(null);
    };

    const resetDisposalForm = () => {
        setDisposalFormData({
            asset_id: "",
            disposal_date: moment().format('YYYY-MM-DD'),
            disposal_type: "Sale",
            disposal_value: "",
            buyer_name: "",
            disposal_remarks: "",
            create_voucher: true
        });
        setSelectedAsset(null);
    };

    const getStatusBadge = (status) => {
        const badges = {
            'Active': 'bg-success',
            'Under Maintenance': 'bg-warning',
            'Disposed': 'bg-secondary',
            'Sold': 'bg-info',
            'Written Off': 'bg-danger'
        };
        return <span className={`badge ${badges[status] || 'bg-secondary'}`}>{status}</span>;
    };

    const columnDefs = [
        {
            headerName: "Asset Code",
            field: "asset_code",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => <strong>{params.value}</strong>
        },
        {
            headerName: "Asset Name",
            field: "asset_name",
            width: 220,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => (
                <div>
                    <div>{params.value}</div>
                    {params.data.location && <small className="text-muted">{params.data.location}</small>}
                </div>
            )
        },
        {
            headerName: "Category",
            field: "category",
            width: 180,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => params.value?.category_name || '-'
        },
        {
            headerName: "Purchase Date",
            field: "purchase_date",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "left" },
            cellRenderer: (params) => moment(params.value).format('DD-MM-YYYY')
        },
        {
            headerName: "Cost",
            field: "purchase_cost",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: (params) => `₹ ${parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`
        },
        {
            headerName: "Book Value",
            field: "book_value",
            width: 150,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "right" },
            cellRenderer: (params) => <strong>₹ {parseFloat(params.value).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</strong>
        },
        {
            headerName: "Age",
            field: "age_years",
            width: 120,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => `${params.value} yrs`
        },
        {
            headerName: "Status",
            field: "status",
            width: 140,
            sortable: true,
            resizable: true,
            filter: true,
            floatingFilter: true,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => getStatusBadge(params.value)
        },
        {
            headerName: "Actions",
            field: "id",
            width: 200,
            sortable: false,
            resizable: true,
            filter: false,
            floatingFilter: false,
            cellStyle: { textAlign: "center" },
            cellRenderer: (params) => {
                const asset = params.data;
                if (asset.status !== 'Active') return '-';
                return (
                    <div>
                        <button className="btn btn-sm btn-info me-1" onClick={() => handleDepreciate(asset)} title="Record Depreciation"><i className="fa fa-calculator"></i></button>
                        <button className="btn btn-sm btn-primary me-1" onClick={() => handleEdit(asset)} title="Edit Asset"><i className="fa fa-edit"></i></button>
                        <button className="btn btn-sm btn-warning me-1" onClick={() => handleDispose(asset)} title="Dispose Asset"><i className="fa fa-trash"></i></button>
                        <button className="btn btn-sm btn-danger" onClick={() => handleDelete(asset)} title="Delete Asset"><i className="fa fa-times"></i></button>
                    </div>
                );
            }
        }
    ];

    return (
        <div>
            <div className="page-header">
                <h2 className="main-content-title tx-24 mg-b-5">Fixed Assets Management</h2>
                <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                        <a>Accounts</a>
                    </li>
                    <li className="breadcrumb-item">
                        <a>Assets</a>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                        Fixed Assets
                    </li>
                </ol>
            </div>

            <div className="content-body">
                <div className="container-fluid">
                    <div className="row">
                        <div className="col-12">
                            <div className="card">
                                <div className="card-header">
                                    <div className="row">
                                        <div className="col-md-6">
                                            <h4 className="card-title">Fixed Assets Management</h4>
                                        </div>
                                        <div className="col-md-6 text-end">
                                    <button
                                        className={`btn btn-sm ${activeTab === 'list' ? 'btn-primary' : 'btn-outline-primary'} me-1`}
                                        onClick={() => setActiveTab('list')}
                                    >
                                        <i className="fa fa-list"></i> All Assets
                                    </button>
                                    <button
                                        className={`btn btn-sm ${activeTab === 'create' ? 'btn-primary' : 'btn-outline-primary'} me-1`}
                                        onClick={() => { resetAssetForm(); setActiveTab('create'); }}
                                    >
                                        <i className="fa fa-plus"></i> Add Asset
                                    </button>
                                    <button
                                        className={`btn btn-sm ${activeTab === 'depreciate' ? 'btn-primary' : 'btn-outline-primary'} me-1`}
                                        onClick={() => {
                                            fetchAssets({ category_id: "", status: 'Active' });
                                            setActiveTab('depreciate');
                                        }}
                                    >
                                        <i className="fa fa-calculator"></i> Depreciation
                                    </button>
                                    <button
                                        className={`btn btn-sm ${activeTab === 'dispose' ? 'btn-primary' : 'btn-outline-primary'}`}
                                        onClick={() => {
                                            fetchAssets({ category_id: "", status: 'Active' });
                                            setActiveTab('dispose');
                                        }}
                                    >
                                        <i className="fa fa-trash"></i> Disposal
                                    </button>
                                </div>
                            </div>
                        </div>
                        <div className="card-body">
                            {/* Asset List Tab */}
                            {activeTab === 'list' && (
                                <>
                                    {/* Filters */}
                                    <div className="card mb-3">
                                        <div className="card-body">
                                            <div className="row">
                                                <div className="col-md-3">
                                                    <label className="form-label">Category</label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="category_id"
                                                        value={filters.category_id}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Categories</option>
                                                        {categories.map((cat) => (
                                                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">Status</label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="status"
                                                        value={filters.status}
                                                        onChange={handleFilterChange}
                                                    >
                                                        <option value="">All Status</option>
                                                        <option value="Active">Active</option>
                                                        <option value="Under Maintenance">Under Maintenance</option>
                                                        <option value="Disposed">Disposed</option>
                                                        <option value="Sold">Sold</option>
                                                        <option value="Written Off">Written Off</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-3">
                                                    <label className="form-label">&nbsp;</label>
                                                    <button
                                                        className="btn btn-secondary w-100"
                                                        onClick={() => {
                                                            setFilters({ category_id: "", status: "Active" });
                                                            fetchAssets({ category_id: "", status: "Active" });
                                                        }}
                                                    >
                                                        Clear Filters
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Assets Table */}
                                    <div className="ag-theme-alpine ag-style mt-2">
                                        <AgGridReact
                                            columnDefs={columnDefs}
                                            rowData={assets}
                                            pagination={true}
                                            paginationPageSize={10}
                                            sortingOrder={["asc", "desc"]}
                                            suppressDragLeaveHidesColumns={true}
                                            domLayout="autoHeight"
                                        />
                                    </div>
                                </>
                            )}

                            {/* Create/Edit Asset Tab */}
                            {activeTab === 'create' && (
                                <div className="card">
                                    <div className="card-header ">
                                        <h5>{editingAsset ? 'Edit' : 'Add'} Fixed Asset</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleAssetSubmit}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Asset Name <span className="text-danger">*</span></label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="asset_name"
                                                        value={assetFormData.asset_name}
                                                        onChange={handleAssetInputChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Category <span className="text-danger">*</span></label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="asset_category_id"
                                                        value={assetFormData.asset_category_id}
                                                        onChange={handleAssetInputChange}
                                                        required
                                                    >
                                                        <option value="">Select Category</option>
                                                        {categories.map((cat) => (
                                                            <option key={cat.id} value={cat.id}>{cat.category_name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Purchase Date <span className="text-danger">*</span></label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="purchase_date"
                                                        value={assetFormData.purchase_date}
                                                        onChange={handleAssetInputChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Purchase Cost <span className="text-danger">*</span></label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control"
                                                        name="purchase_cost"
                                                        value={assetFormData.purchase_cost}
                                                        onChange={handleAssetInputChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Salvage Value</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control"
                                                        name="salvage_value"
                                                        value={assetFormData.salvage_value}
                                                        onChange={handleAssetInputChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12 mb-3">
                                                    <label className="form-label">Location</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="location"
                                                        value={assetFormData.location}
                                                        onChange={handleAssetInputChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Serial Number</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="serial_number"
                                                        value={assetFormData.serial_number}
                                                        onChange={handleAssetInputChange}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Manufacturer</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="manufacturer"
                                                        value={assetFormData.manufacturer}
                                                        onChange={handleAssetInputChange}
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Model</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="model"
                                                        value={assetFormData.model}
                                                        onChange={handleAssetInputChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Warranty (Months)</label>
                                                    <input
                                                        type="number"
                                                        className="form-control"
                                                        name="warranty_months"
                                                        value={assetFormData.warranty_months}
                                                        onChange={handleAssetInputChange}
                                                    />
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Remarks</label>
                                                    <textarea
                                                        className="form-control"
                                                        name="remarks"
                                                        value={assetFormData.remarks}
                                                        onChange={handleAssetInputChange}
                                                        rows="2"
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12 text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary me-2"
                                                        onClick={() => { resetAssetForm(); setActiveTab('list'); }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button type="submit" className="btn btn-primary">
                                                        <i className="fa fa-save"></i> {editingAsset ? 'Update' : 'Create'} Asset
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* Record Depreciation Tab */}
                            {activeTab === 'depreciate' && (
                                <div className="card">
                                    <div className="card-header ">
                                        <h5>Record Depreciation</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleDepreciationSubmit}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Select Asset <span className="text-danger">*</span></label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="asset_id"
                                                        value={depreciationFormData.asset_id}
                                                        onChange={handleDepreciationInputChange}
                                                        required
                                                    >
                                                        <option value="">Select Asset</option>
                                                        {assets.filter(a => a.status === 'Active').map((asset) => (
                                                            <option key={asset.id} value={asset.id}>
                                                                {asset.asset_code} - {asset.asset_name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Financial Year <span className="text-danger">*</span></label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="financial_year_id"
                                                        value={depreciationFormData.financial_year_id}
                                                        onChange={handleDepreciationInputChange}
                                                        required
                                                    >
                                                        <option value="">Select Financial Year</option>
                                                        {financialYears.map((year) => (
                                                            <option key={year.id} value={year.id}>{year.year_name}</option>
                                                        ))}
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Depreciation Date <span className="text-danger">*</span></label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="depreciation_date"
                                                        value={depreciationFormData.depreciation_date}
                                                        onChange={handleDepreciationInputChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Period</label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="period"
                                                        value={depreciationFormData.period}
                                                        onChange={handleDepreciationInputChange}
                                                    >
                                                        <option value="Monthly">Monthly</option>
                                                        <option value="Quarterly">Quarterly</option>
                                                        <option value="Annual">Annual</option>
                                                    </select>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">&nbsp;</label>
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            name="create_voucher"
                                                            checked={depreciationFormData.create_voucher}
                                                            onChange={handleDepreciationInputChange}
                                                        />
                                                        <label className="form-check-label">Create Accounting Voucher</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12 mb-3">
                                                    <label className="form-label">Remarks</label>
                                                    <textarea
                                                        className="form-control"
                                                        name="remarks"
                                                        value={depreciationFormData.remarks}
                                                        onChange={handleDepreciationInputChange}
                                                        rows="2"
                                                    ></textarea>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12 text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary me-2"
                                                        onClick={() => { resetDepreciationForm(); setActiveTab('list'); }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button type="submit" className="btn btn-primary">
                                                        <i className="fa fa-calculator"></i> Record Depreciation
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {/* Record Disposal Tab */}
                            {activeTab === 'dispose' && (
                                <div className="card">
                                    <div className="card-header">
                                        <h5>Record Asset Disposal</h5>
                                    </div>
                                    <div className="card-body">
                                        <form onSubmit={handleDisposalSubmit}>
                                            <div className="row">
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Select Asset <span className="text-danger">*</span></label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="asset_id"
                                                        value={disposalFormData.asset_id}
                                                        onChange={handleDisposalInputChange}
                                                        required
                                                    >
                                                        <option value="">Select Asset</option>
                                                        {assets.filter(a => a.status === 'Active').map((asset) => (
                                                            <option key={asset.id} value={asset.id}>
                                                                {asset.asset_code} - {asset.asset_name} (Book Value: ₹{parseFloat(asset.book_value).toLocaleString('en-IN')})
                                                            </option>
                                                        ))}
                                                    </select>
                                                </div>
                                                <div className="col-md-6 mb-3">
                                                    <label className="form-label">Disposal Type <span className="text-danger">*</span></label>
                                                    <select
                                                className="default-select  form-control wide"
                                                        name="disposal_type"
                                                        value={disposalFormData.disposal_type}
                                                        onChange={handleDisposalInputChange}
                                                        required
                                                    >
                                                        <option value="Sale">Sale</option>
                                                        <option value="Scrap">Scrap</option>
                                                        <option value="Donation">Donation</option>
                                                        <option value="Trade-in">Trade-in</option>
                                                        <option value="Write-off">Write-off</option>
                                                    </select>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Disposal Date <span className="text-danger">*</span></label>
                                                    <input
                                                        type="date"
                                                        className="form-control"
                                                        name="disposal_date"
                                                        value={disposalFormData.disposal_date}
                                                        onChange={handleDisposalInputChange}
                                                        required
                                                    />
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Disposal Value</label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        className="form-control"
                                                        name="disposal_value"
                                                        value={disposalFormData.disposal_value}
                                                        onChange={handleDisposalInputChange}
                                                    />
                                                    <small className="text-muted">Sale price or scrap value</small>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">Buyer Name</label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="buyer_name"
                                                        value={disposalFormData.buyer_name}
                                                        onChange={handleDisposalInputChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-8 mb-3">
                                                    <label className="form-label">Disposal Remarks</label>
                                                    <textarea
                                                        className="form-control"
                                                        name="disposal_remarks"
                                                        value={disposalFormData.disposal_remarks}
                                                        onChange={handleDisposalInputChange}
                                                        rows="2"
                                                    ></textarea>
                                                </div>
                                                <div className="col-md-4 mb-3">
                                                    <label className="form-label">&nbsp;</label>
                                                    <div className="form-check">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            name="create_voucher"
                                                            checked={disposalFormData.create_voucher}
                                                            onChange={handleDisposalInputChange}
                                                        />
                                                        <label className="form-check-label">Create Accounting Voucher</label>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="alert alert-warning">
                                                <strong>Warning:</strong> Disposing an asset will mark it as disposed and it cannot be undone. The system will calculate gain/loss automatically.
                                            </div>
                                            <div className="row">
                                                <div className="col-md-12 text-end">
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary me-2"
                                                        onClick={() => { resetDisposalForm(); setActiveTab('list'); }}
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button type="submit" className="btn btn-primary">
                                                        <i className="fa fa-trash"></i> Record Disposal
                                                    </button>
                                                </div>
                                            </div>
                                        </form>
                                    </div>
                                </div>
                            )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
